import {fileLevelCommentKey, type DiffEntry, type FileKey, type LineKey} from '@github-ui/diff-lines/types'
import type {PullRequestFileTreeDiff, ThreadMarker} from '../page-data/payloads/file-tree'
import type {FileMarkers, Markers, MarkersByFile, MarkersInfo} from '../stores/slices/markers'
import type {DiffAnnotation, ThreadPositioning} from '@github-ui/diff-lines/conversation-types'
import type {PendingReview} from '../stores/slices/pending-review'
import type {CreateReviewCommentMutationData} from '../mutations/use-create-review-comment-mutation'
import type {DiffFilterState} from '../stores/slices/diff-filter'
import {getFileExtension} from '@github-ui/diff-file-tree/diff-file-tree-helpers'

export const OUTSIDE_DIFF_KEY = '__OUTSIDE_DIFF__' as FileKey

export function extractAnnotationMarkerId(
  parsedAnnotationId: number,
  annotations: Record<number, Pick<DiffAnnotation, 'id' | 'databaseId'>> | undefined,
) {
  if (!parsedAnnotationId || !annotations) {
    return undefined
  }

  for (const annotation of Object.values(annotations)) {
    if (annotation && annotation.databaseId === parsedAnnotationId) {
      return annotation.id
    }
  }

  return undefined
}

export function extractCommentThreadMarkerId(parsedCommentId: number | undefined, markers: MarkersByFile | undefined) {
  if (!parsedCommentId || !markers) {
    return undefined
  }

  for (const marker of Object.values(markers)) {
    for (const thread of marker.threads) {
      for (const comment of thread.commentsData?.comments ?? []) {
        if (comment.databaseId === parsedCommentId) {
          return thread.id
        }
      }
    }
  }

  return undefined
}

export function buildDiffSummaryMap(diffSummaries: PullRequestFileTreeDiff[]): Record<string, PullRequestFileTreeDiff> {
  return diffSummaries.reduce<Record<string, PullRequestFileTreeDiff>>((buildingDiffSummaryMap, diffSummary) => {
    buildingDiffSummaryMap[diffSummary.path] = diffSummary
    return buildingDiffSummaryMap
  }, {})
}

export function buildCollapsedDiffStatusMap(diffSummaries: PullRequestFileTreeDiff[]): Record<string, boolean> {
  return diffSummaries.reduce<Record<string, boolean>>((buildingCollapsedDiffStatusMap, diffSummary) => {
    buildingCollapsedDiffStatusMap[diffSummary.path] = !!diffSummary.markedAsViewed
    return buildingCollapsedDiffStatusMap
  }, {})
}

export function buildPendingReview(pendingReviewData: PendingReview) {
  const pendingThreadIDs = pendingReviewData.comments.map(comment => parseInt(comment.threadId))
  return {
    id: pendingReviewData.id,
    pendingReviewIDs: pendingThreadIDs,
    comments: pendingReviewData.comments,
  }
}

export function buildDiffEntryMap(diffEntries: DiffEntry[]): Record<string, DiffEntry> {
  return diffEntries.reduce<Record<string, DiffEntry>>((buildingDiffEntryMap, diffEntry) => {
    buildingDiffEntryMap[diffEntry.path] = diffEntry
    return buildingDiffEntryMap
  }, {})
}

export function buildNormalizedMarkers(
  markers: Markers | undefined,
  diffSummaries: PullRequestFileTreeDiff[],
): MarkersInfo | undefined {
  if (!markers) {
    return undefined
  }

  const normalizedMarkers: MarkersInfo = {}
  if (!diffSummaries) return normalizedMarkers

  // Track thread IDs to identify threads outside the current diff
  const unpositionedThreadIds = new Set(Object.keys(markers.threads).map(id => Number(id)))

  // TODO: eventually we will keep the comment counts for threads on the thread object itself, but for now, we don't
  for (const diffSummary of diffSummaries) {
    if (!diffSummary.markersMap || Object.keys(diffSummary.markersMap).length === 0) continue
    const filePath = diffSummary.path
    const currentFileMarkers: MarkersByFile = {}
    for (const markerKey in diffSummary.markersMap) {
      const diffSummaryMarker = diffSummary.markersMap[markerKey]
      if (!diffSummaryMarker?.threads && !diffSummaryMarker?.annotations) continue

      const threadsAnnotationsInLine: FileMarkers = {
        threads: [],
        annotations: [],
      }
      if (diffSummaryMarker.annotations) {
        for (const threadMarker of diffSummaryMarker.annotations) {
          const annotation = markers.annotations[Number(threadMarker.id)]
          if (annotation) {
            //totalCommentCountForSummary += thread.commentsData.comments.length
            threadsAnnotationsInLine.annotations.push(annotation)
          }
        }
      }
      if (diffSummaryMarker.threads) {
        for (const threadMarker of diffSummaryMarker.threads) {
          const threadId = Number(threadMarker.id)
          const positioning = getPositioningValuesFromThreadMarkers(markerKey, threadMarker, diffSummary.path)
          let thread = markers.threads[threadId]
          if (thread && positioning) {
            // TODO: This is a workaround to avoid the ESLint error about mutating props directly.
            // Ideally, we should refactor this to avoid direct mutation, but since we need to do a quick fix for performance, this mimics the previous behavior.

            thread = {...thread, positioning, subject: positioning, shouldRenderInDiffLines: true} // stitch in both until we can remove subject
          } else if (thread) {
            thread = {...thread, path: diffSummary.path, shouldRenderInDiffLines: false} // stitch in the path for any thread that doesn't have a position, so that we can at least group them by file.
          } else {
            // Thread doesn't have full data yet - create minimal thread object with just id and positioning
            thread = {
              id: threadId.toString(),
              positioning: positioning
                ? positioning
                : {path: diffSummary.path, isOutdated: !!threadMarker.outdatedReason},
              subject: positioning,
              hideUntilLoadedAndInView: true, // don't show in diff until it's loaded and in view to avoid CLS issues
              shouldRenderInDiffLines: !!positioning, // only render in diff lines if we have positioning data, otherwise render outside of diff
            }
          }
          if (thread) {
            threadsAnnotationsInLine.threads.push(thread)
            // Mark this thread as processed
            unpositionedThreadIds.delete(threadId)
          }
        }
      }

      if (markerKey === 'FILE') {
        // because of typescript weirdness, we need to use a key that follows the FileKey type
        currentFileMarkers[fileLevelCommentKey] = threadsAnnotationsInLine
      } else {
        currentFileMarkers[markerKey as LineKey | FileKey] = threadsAnnotationsInLine
      }
    }

    normalizedMarkers[filePath] = currentFileMarkers
  }

  // Add threads that exist outside the current diff view
  if (unpositionedThreadIds.size > 0) {
    const threadsOutsideDiff: MarkersByFile = {}
    for (const threadId of unpositionedThreadIds) {
      const thread = markers.threads[threadId]
      if (thread) {
        // Use a special key for threads outside current diff
        const key = OUTSIDE_DIFF_KEY
        if (!threadsOutsideDiff[key]) {
          threadsOutsideDiff[key] = {threads: [], annotations: []}
        }
        threadsOutsideDiff[key].threads.push(thread)
      }
    }
    normalizedMarkers[OUTSIDE_DIFF_KEY] = threadsOutsideDiff
  }

  return normalizedMarkers
}

export function buildSubjectFromCommentData(inputs: CreateReviewCommentMutationData): ThreadPositioning | undefined {
  return {
    endLine: inputs.line,
    endDiffSide: inputs.side?.toLowerCase() === 'right' ? 'RIGHT' : 'LEFT',
    path: inputs.path,
    startLine: inputs.startLine ?? inputs.line,
    startDiffSide: inputs.startSide
      ? inputs.startSide?.toLowerCase() === 'right'
        ? 'RIGHT'
        : 'LEFT'
      : inputs.side?.toLowerCase() === 'right'
        ? 'RIGHT'
        : 'LEFT',
    isOutdated: false, // false because it's a new comment
  }
}

const diffSideMap = {
  L: 'LEFT',
  R: 'RIGHT',
} as const

type LineSideLetter = 'L' | 'R'

function getPositioningValuesFromThreadMarkers(
  diffSummaryKey: string,
  threadMarker: ThreadMarker,
  diffSummaryPath: string,
): ThreadPositioning | undefined {
  if (diffSummaryKey === 'FILE') {
    return undefined
  }

  const endDiffSideChar = diffSummaryKey[0]
  if (!isLineSideLetter(endDiffSideChar)) {
    return undefined
  }
  const endDiffSideLetter = endDiffSideChar
  const endLineNumber = Number(diffSummaryKey.slice(1))

  let startLineNumber = -1
  let startDiffSideLetter: LineSideLetter = 'R'

  if (threadMarker.start) {
    startLineNumber = parseInt(threadMarker.start.slice(1))
    const startChar = threadMarker.start[0]
    if (isLineSideLetter(startChar)) {
      startDiffSideLetter = startChar
    }
  } else {
    startLineNumber = endLineNumber
    startDiffSideLetter = endDiffSideLetter
  }

  const endDiffSide = diffSideMap[endDiffSideLetter]
  const startDiffSide = diffSideMap[startDiffSideLetter]

  return {
    endLine: endLineNumber,
    endDiffSide,
    path: diffSummaryPath,
    startLine: startLineNumber,
    startDiffSide,
    isOutdated: !!threadMarker.outdatedReason,
  }
}

function isLineSideLetter(value: string | undefined): value is LineSideLetter {
  return value === 'L' || value === 'R'
}

export function matchesDiffFilters(
  diff: PullRequestFileTreeDiff,
  filters: DiffFilterState,
  isOwnedByViewer: boolean,
): boolean {
  return (
    diff.path.toLowerCase().includes(filters.filterText.toLowerCase()) &&
    !filters.unselectedFileExtensions.has(getFileExtension(diff.path)) &&
    (filters.showDeletedFiles || !(diff.changeType === 'REMOVED' || diff.changeType === 'DELETED')) &&
    (!filters.showOnlyManifestFiles || !!diff.isManifestFile) &&
    (!filters.showOnlyOwnedFiles || isOwnedByViewer) &&
    (filters.showViewedFiles || !diff.markedAsViewed) &&
    (filters.showVendoredFiles || !diff.isVendored)
  )
}

export function clearDiffFilterSearchParams(searchParams: URLSearchParams): URLSearchParams {
  searchParams.delete('file-filters[]')
  searchParams.delete('owned-by[]')
  searchParams.delete('show-deleted-files')
  searchParams.delete('manifests')
  searchParams.delete('show-vendored-files')
  searchParams.delete('show-viewed-files')

  return searchParams
}
